// Les réglages du mode fun — cinq interrupteurs, un skin et la musique des skins —, et le seul
// endroit qui sait où ils sont rangés.
//
// Dans le `localStorage` et pas en base : ce sont des réglages d'APPARENCE, ils appartiennent au
// navigateur où l'on regarde l'écran, pas au compte. Le même raisonnement que les records de
// l'arcade — un curseur de confettis n'a rien à faire dans la même base que des contrats.
//
// Le mode lui-même, lui, reste en base (`users.arcade_mode`) : on déverrouille une fois, pas
// une fois par machine.

import { commitTheme, resolveTheme } from "./theme_transition"

export const FUN_SETTINGS_EVENT = "fun:settings"
// Annoncé par la musique quand elle démarre ou s'arrête (cf. fun_music.js).
export const FUN_MUSIC_EVENT = "fun:music"
// `scenery` : le décor vivant des skins (cf. utils/fun_scenes.js et `fun-skin-still`).
export const FUN_SWITCHES = ["icons", "confetti", "micro", "sounds", "scenery"]

// Partagée avec le script de démarrage du layout (layouts/_body.html.erb), qui pose le skin
// avant le premier rendu.
const STORAGE_KEY = "depozen.fun-mode"

// Le localStorage peut être indisponible (navigation privée, stockage bloqué) ou contenir
// n'importe quoi. Dans les deux cas on repart d'un objet vide sans rien casser.
function readStored() {
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}")
    return stored && typeof stored === "object" ? stored : {}
  } catch (error) {
    return {}
  }
}

// Fusionne dans ce qui est rangé plutôt que de le remplacer : les interrupteurs et le skin
// partagent la même clé, et couper les confettis ne doit pas enlever le skin.
function store(patch) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...readStored(), ...patch }))
  } catch (error) {
    // Réglage perdu au prochain chargement, mais appliqué tout de suite : mieux que rien.
    console.warn("[fun] réglages non enregistrés", error)
  }
}

// Tout est allumé tant qu'on n'a rien coupé : le mode se mérite, il ne se configure pas à
// l'arrivée.
export function readFunSettings() {
  const stored = readStored()
  return Object.fromEntries(FUN_SWITCHES.map((key) => [key, stored[key] !== false]))
}

export function writeFunSettings(settings) {
  const merged = { ...readFunSettings(), ...settings }

  store(merged)
  applyFunSettings(merged)
  window.dispatchEvent(new CustomEvent(FUN_SETTINGS_EVENT, { detail: merged }))
  return merged
}

// Les attributs `data-fun-*` du body sont l'interface entre ce fichier et la feuille de style
// (cf. components/_fun_mode.scss) : le JS ne pose jamais de style, il pose un état.
export function applyFunSettings(settings = readFunSettings()) {
  const body = document.body
  if (!body) return settings

  FUN_SWITCHES.forEach((key) => {
    body.dataset[`fun${key.charAt(0).toUpperCase()}${key.slice(1)}`] = settings[key] ? "on" : "off"
  })

  return settings
}

// --- la musique ---------------------------------------------------------------------------------

// La musique des skins (cf. utils/fun_music.js) : allumée tant qu'on ne l'a pas coupée, comme les
// interrupteurs, et un seul choix pour tous les skins. Elle ne se règle pas dans la salle d'arcade
// mais sur le bouton de la barre latérale, le seul qu'on a sous la main quand elle joue.
export function readFunMusic() {
  return readStored().music !== false
}

export function writeFunMusic(on) {
  store({ music: Boolean(on) })
}

// --- les skins ----------------------------------------------------------------------------------

// La liste vient du serveur (FunModeHelper::FUN_SKINS), publiée par le script de démarrage du
// layout avec le thème de chaque skin : { terminal: "dark", xp: "light", … }. Hors mode fun elle
// est vide, et aucun skin ne peut se poser.
export function funSkins() {
  return window.__DEPOZEN_FUN_SKINS__ || {}
}

function knownSkin(skin) {
  return typeof skin === "string" && Object.prototype.hasOwnProperty.call(funSkins(), skin)
    ? skin
    : null
}

// `null` = la plateforme telle qu'elle est.
export function readFunSkin() {
  return knownSkin(readStored().skin)
}

// Deux attributs sur le body, et le thème du skin imposé par-dessus la préférence du compte ;
// quand on l'enlève, la préférence (`data-theme-preference`, rendue par le serveur) reprend la
// main. Idempotent, et rejoué à chaque connexion du contrôleur (cf. fun_mode_controller.js).
//
// Sans skin à poser ni à retirer, on ne touche à RIEN, thème compris : d'autres que nous le
// gèrent (la préférence « auto », le pont Hotwire Native), et ce n'est pas au mode fun de le
// réécrire à chaque visite.
export function applyFunSkin(skin = readFunSkin()) {
  const body = document.body
  if (!body) return null

  const current = knownSkin(skin)

  if (current) {
    const theme = funSkins()[current]
    body.dataset.funSkin = current
    body.dataset.funSkinTheme = theme
    commitTheme(theme)
  } else if (body.dataset.funSkin) {
    delete body.dataset.funSkin
    delete body.dataset.funSkinTheme
    commitTheme(resolveTheme(body.dataset.themePreference || "light"))
  }

  return current
}

// Changer de skin est le seul geste du mode qui transforme TOUT l'écran d'un coup : il passe
// donc par une View Transition, comme la bascule clair/sombre, et `html[data-fun-skin-next]`
// laisse à chaque skin le choix de son entrée (cf. components/_fun_skins.scss).
export function writeFunSkin(skin) {
  const next = knownSkin(skin)
  store({ skin: next })

  const swap = () => {
    applyFunSkin(next)
    window.dispatchEvent(new CustomEvent(FUN_SETTINGS_EVENT, { detail: { ...readFunSettings(), skin: next } }))
  }

  const root = document.documentElement
  const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  if (typeof document.startViewTransition !== "function" || reduceMotion) {
    swap()
    return next
  }

  root.classList.add("theme-transitioning")
  root.dataset.funSkinNext = next || "classic"
  document.startViewTransition(swap).finished.finally(() => {
    root.classList.remove("theme-transitioning")
    delete root.dataset.funSkinNext
  })
  return next
}
